import { KatapultDialog } from '../elements/katapult-elements/katapult-dialog.js';
import { html } from 'lit';

const TOKEN_PREFIX_DELIMITER = '$(';
const TOKEN_SUFFIX_DELIMITER = ')';
const TOKEN_REGEX = /(?<=\$\()[_\-a-zA-Z0-9]+(?=\))/g;
const SELECTION_TOKENS = ['node_id', 'connection_id', 'section_id'];

/**
 * The element an embedded web tool iframe dialog centers on. The host sets this so callers never thread a dom
 * element through every web tool call.
 * @type {Element?}
 */
let iframeTarget = null;

/**
 * Sets the element embedded web tool iframes center on. The host picks the active map, so web tool callers never
 * pass a center element themselves.
 * @param {Element?} element
 */
function setIframeTarget(element) {
  iframeTarget = element;
}

/**
 * Given a tokenized url, a set unique tokens will be returned.
 * @param {string} url
 * @returns {string[]}
 * @throws {TypeError} If url is not a string.
 */
function getTokensFromUrl(url) {
  if (typeof url !== 'string') throw new TypeError('url must be a string');
  const iterator = url.matchAll(TOKEN_REGEX);
  const allTokenInstances = Array.from(iterator, (match) => match[0]);
  const uniqueTokenInstances = [...new Set(allTokenInstances)];
  return uniqueTokenInstances;
}

/**
 * Given a tokenized url and an array of resolved tokens, a url will be returned where all tokens are replaced with their resolved values.
 * @param {string} url
 * @param {Object<string, string>} tokenMapping - An object where the keys are tokens and the values are the resolved values.
 * @returns {string} - The url in which all tokens have been replaced with their resolved values.
 * @throws {TypeError} If url is not a string.
 */
function replaceUrlTokens(url, tokenMapping) {
  if (typeof url !== 'string') throw new TypeError('url must be a string');
  for (const [token, value] of Object.entries(tokenMapping)) {
    const enclosedToken = `${TOKEN_PREFIX_DELIMITER}${token}${TOKEN_SUFFIX_DELIMITER}`;
    url = url.replaceAll(enclosedToken, encodeURIComponent(value));
  }
  return url;
}

/**
 * Prompts the user to confirm that they want to run a tool unless they have previously confirmed that they wish to run the tool.
 * @param {string} toolId
 * @returns {Promise<boolean>}
 * @throws {TypeError} If toolId is not a string.
 * @throws {Error} If toolId is an empty string.
 */
async function confirmMayRunTool(toolId) {
  if (typeof toolId !== 'string') throw new TypeError('toolId must be a string');
  if (toolId.length === 0) throw new Error('toolId must not be an empty string');

  const { uid } = globalThis.firebase.auth().currentUser ?? {};

  const toolLocalStoragePath = ['may_run_api_tool_from_model_without_confirmation', uid, toolId].filter((x) => !!x).join('_');

  const mayRunToolWithoutConfirmation = localStorage.getItem(toolLocalStoragePath) === 'true';
  if (mayRunToolWithoutConfirmation) return true;

  const shouldRunTool = await KatapultDialog.confirm({
    text: `This tool will send data to an external source outside of this application. Be sure that you trust the creator of this tool.`,
    confirmButton: { label: `Continue` }
  }).confirmed;

  if (shouldRunTool) {
    localStorage.setItem(toolLocalStoragePath, 'true');
    return true;
  }

  return false;
}

/**
 * @typedef {Object} IframeConfig
 * @property {string} width - The width of the iframe dialog.
 * @property {string} height - The height of the iframe dialog.
 * @property {string} minWidth - The minimum width of the iframe dialog.
 * @property {string} minHeight - The minimum height of the iframe dialog.
 * @property {string} maxWidth - The maximum width of the iframe dialog.
 * @property {string} maxHeight - The maximum height of the iframe dialog.
 * @property {boolean} interactive - Whether the iframe dialog is resizable and draggable
 * @property {string} theme_color - The theme color for the iframe header.
 * @property {string} text_color - The text color for the iframe header.
 * @property {boolean} [pinnable] - Whether the dialog can be pinned (shows pin button)
 * @property {string} [pin_location] - The default pin location: 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'bottom-middle', 'auto-pin'
 * @property {boolean} [pin_in_map] - Whether the dialog is pinned in the map (if true, iframeTarget is required)
 * @property {boolean} [toggle_border] - Whether the dialog shows border toggle functionality
 */

/**
 * @typedef {Object} WebToolConfig
 * @property {string} [id] - The id of the tool.
 * @property {string} label - The name of the tool.
 * @property {string} endpoint_url - The url of the tool.
 * @property {string} endpoint_action - Whether the tool should be opened in a new tab or not.
 * @property {IframeConfig} [iframe_config] - Configuration for iframe display.
 */

/**
 * Performs a web tool's configured endpoint action with an already resolved url. The caller is responsible for
 * building the url and confirming the user trusts the tool before calling this.
 * @param {string} resolvedUrl - The tool url with all tokens already replaced.
 * @param {WebToolConfig} toolConfig
 * @returns {Promise<void>}
 * @throws {Error} If endpoint_action is missing or unknown.
 */
async function runWebTool(resolvedUrl, toolConfig) {
  const { label = 'Web Tool', endpoint_action: endpointAction, iframe_config: iframeConfig } = toolConfig;
  if (!endpointAction) throw new Error('endpoint_action is missing from the tool config');

  // Perform the configured endpoint action with the resolved url
  switch (endpointAction) {
    case 'API_CALL':
      await fetch(resolvedUrl, { method: 'POST' }).then(() => {
        KatapultDialog.alert({ text: `${label} was run successfully`, dialog: { title: `${label} Succeeded`, icon: 'check_circle' } });
      });
      break;
    case 'OPEN_LINK_IN_NEW_TAB':
      openWindow(resolvedUrl);
      break;
    case 'OPEN_LINK_IN_EMBEDDED_WINDOW':
      openInIframe(resolvedUrl, label, iframeConfig);
      break;
    default:
      throw new Error(`Unknown endpoint_action: ${endpointAction}`);
  }
}

/**
 * Resolves a single web tool url token to its value, fetching the current user's Katapult Pro API key on demand.
 * Unknown tokens resolve to an empty string so they are stripped from the url.
 * @param {string} token
 * @param {string} [jobId] - The active job id, used for the job_id token.
 * @param {string} [selectedItemId] - The selected item id, used for the node_id, connection_id, or section_id token.
 * @returns {Promise<string | null>}
 */
async function resolveTokenValue(token, jobId, selectedItemId) {
  if (token === 'job_id') return jobId ?? '';
  if (SELECTION_TOKENS.includes(token)) return selectedItemId ?? '';
  if (token !== 'api_key') return '';

  // Fetch the api key, returning null if it cannot be fetched
  const apiKeyResult = await globalThis.firebase
    .functions()
    .httpsCallable('apiKeyManagement')({ action: 'fetch' })
    .catch(() => null);
  return apiKeyResult?.data?.key ?? null;
}

/**
 * Builds a runnable web tool url by resolving its config's tokens against the supplied values. The api key token
 * is fetched on demand and a missing key throws so the caller can report it.
 * @param {WebToolConfig} toolConfig
 * @param {string} [jobId] - The active job id, used for the job_id token.
 * @param {string} [selectedItemId] - The selected item id, used for the node_id, connection_id, or section_id token.
 * @returns {Promise<string>}
 * @throws {Error} If endpoint_url is missing or a required api key cannot be resolved.
 */
async function buildWebToolUrl(toolConfig, jobId, selectedItemId) {
  const { endpoint_url: endpointUrl } = toolConfig;
  if (!endpointUrl) throw new Error('endpoint_url is missing from the tool config');

  // Resolve every token in the url to its value, fetching the api key only when the url asks for one
  const tokens = getTokensFromUrl(endpointUrl);
  const tokenValuePromises = tokens.map((tokenId) => resolveTokenValue(tokenId, jobId, selectedItemId));
  const tokenValues = await Promise.all(tokenValuePromises);
  const tokenMapping = Object.fromEntries(tokenValues.map((value, index) => [tokens[index], value]));

  // A required api key that could not be fetched must not be silently sent as an empty token
  if ('api_key' in tokenMapping && tokenMapping.api_key == null)
    throw new Error('This tool requires a valid Katapult Pro API key to run. Please ensure you have a valid API key and try again.');

  return replaceUrlTokens(endpointUrl, tokenMapping);
}

/**
 * Returns the job entity type a web tool's url requires the user to select, or undefined when it needs none.
 * Callers use this to prompt for the selection before running the tool.
 * @param {WebToolConfig} toolConfig
 * @returns {string | undefined} 'node', 'connection', or 'section'
 */
function getRequiredSelectionType(toolConfig) {
  const tokens = getTokensFromUrl(toolConfig?.endpoint_url ?? '');
  const selectionToken = tokens.find((token) => SELECTION_TOKENS.includes(token));
  return selectionToken?.split('_')[0];
}

/**
 * Runs a web tool from its tool config. The caller resolves the active job and prompts for any required item
 * selection first, passing the results in. Confirms the user trusts the tool, builds its url, then runs it.
 * @param {WebToolConfig} toolConfig
 * @param {string} jobId - The id of the active job, used for the job_id token.
 * @param {string} [selectedItemId] - The id of the selected item, used for a selection token.
 * @returns {Promise<void>}
 */
async function runWebToolFromToolConfig(toolConfig, jobId, selectedItemId) {
  const { label = 'Web Tool', id = label } = toolConfig;

  // Confirm the user trusts the external tool before doing any work
  const mayRunTool = await confirmMayRunTool(id);
  if (!mayRunTool) return;

  try {
    // Build the resolved url, then run the tool with it
    const resolvedUrl = await buildWebToolUrl(toolConfig, jobId, selectedItemId);
    await runWebTool(resolvedUrl, toolConfig);
  } catch (error) {
    KatapultDialog.alert({
      text: `An error occurred while running ${label}:\n\n${error.message}`,
      dialog: { title: `${label} Failed`, icon: 'error', color: 'var(--sl-color-danger)' }
    });
  }
}

/**
 * Opens the url in a new tab
 * @param {string} url
 */
function openWindow(url) {
  const urlProtocol = url.match(new RegExp('.+://'))?.[0];
  const urlHasProtocol = urlProtocol !== undefined;
  const protocolStartsWithHttps = !!urlProtocol?.startsWith('https');
  // If we have a non https protocol, open the url in the same tab using location.href
  if (urlHasProtocol == true && protocolStartsWithHttps == false) {
    window.location.href = url;
  }
  // Otherwise, open it in a new tab
  else {
    window.open(url, '_blank');
  }
}

/**
 * Opens an iframe with the specified URL and configuration.
 * @param {string} url - the URL to open in the iframe
 * @param {string} title - the title of the iframe dialog
 * @param {IframeConfig} [config] - configuration for the iframe dialog. If not provided, defaults will be used.
 * @returns {Promise<KatapultDialog>} - a Promise that resolved with the opened dialog instance
 */
async function openInIframe(url, title = 'Web Tool', config) {
  const {
    width = '30%',
    height = '30%',
    minWidth = '20%',
    minHeight = '20%',
    maxWidth = '95%',
    maxHeight = '95%',
    interactive = true,
    theme_color = '#007299',
    text_color = '#F2F2F2',
    pinnable = false,
    pin_location = null,
    pin_in_map = false,
    toggle_border = false
  } = config || {};

  // Check if an iframe with the same title already exists
  /** @type KatapultDialog? */
  const existingDialog = document.querySelector(`katapult-dialog[data-iframe-title="${title}"]`);
  if (existingDialog) {
    // Bring existing dialog to front and pulse
    existingDialog.resetPosition();
    existingDialog.pulse();
    return existingDialog;
  }

  // Convert percentage dimensions to pixel values for KatapultDialog
  const { finalWidth, finalHeight } = convertDimensionsToPixels(width, height);
  const { finalWidth: finalMinWidth, finalHeight: finalMinHeight } = convertDimensionsToPixels(minWidth, minHeight);
  const { finalWidth: finalMaxWidth, finalHeight: finalMaxHeight } = convertDimensionsToPixels(maxWidth, maxHeight);

  // Handle auto-pin functionality from dropdown
  const isAutoPin = pin_location === 'auto-pin';
  const finalPinLocation = isAutoPin ? null : pin_location;

  // Detect mobile devices and adjust width accordingly
  const mobileWidth = Math.round(window.innerWidth * 0.75);
  const responsiveWidth = globalThis.isMobile ? mobileWidth : finalWidth;

  const dialog = KatapultDialog.open({
    dialog: {
      title: title,
      color: theme_color,
      textColor: text_color,
      closeButton: true,
      draggable: interactive,
      width: responsiveWidth,
      height: finalHeight,
      minWidth: finalMinWidth,
      minHeight: finalMinHeight,
      maxWidth: finalMaxWidth,
      maxHeight: finalMaxHeight,
      closeOnEscape: true,
      closeOnOutsideClick: false,
      modal: false,
      resizable: interactive,
      centerElement: pin_in_map ? iframeTarget : null,
      boundaryElement: pin_in_map && iframeTarget ? iframeTarget : null,
      pinnable: pinnable,
      pinLocation: finalPinLocation,
      autoPin: isAutoPin,
      toggleBorder: toggle_border
    },
    template: () => html`
      <div
        slot="before-scroll"
        style="width: 100%; height: 100%; display: flex; flex-direction: column; overflow: hidden; position: relative;"
      >
        <iframe
          src="${url}"
          style="width: 100%; height: 100%; border: none; display: block; background: white; position: relative; z-index: 1; min-width: ${finalMinWidth}px; min-height: ${finalMinHeight}px; flex: 1;"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-popups-to-escape-sandbox"
        ></iframe>
      </div>
    `
  });

  // Add identifier for reuse detection
  dialog.setAttribute('data-iframe-title', title);

  await dialog.openStart;
  const frameWindow = dialog.querySelector('iframe')?.contentWindow;
  const messageListener = (e) => {
    if (e.source !== frameWindow) return;
    if (e.data?.katapultDialogAction === 'close') dialog.close();
  };
  window.addEventListener('message', messageListener);
  dialog.closeComplete.then(() => window.removeEventListener('message', messageListener));

  return dialog;
}

/**
 * Converts width and height values to pixel values.
 * @param {string|number} width - the width value, can be a percentage string, pixel string, or number
 * @param {string|number} height - the height value, can be a percentage string, pixel string, or number
 * @returns {{ finalWidth: number, finalHeight: number }} - an object containing the final width and height in pixels
 */
function convertDimensionsToPixels(width, height) {
  // Helper function to convert percentage strings to pixel values
  const convertPercentageToPixels = (value, dimension) => {
    if (typeof value === 'string' && value.includes('%')) {
      const percentage = parseInt(value.replace('%', ''));
      const maxDimension = dimension === 'width' ? window.innerWidth : window.innerHeight;
      return Math.round((percentage / 100) * maxDimension);
    }
    if (typeof value === 'string' && value.includes('px')) {
      return parseInt(value.replace('px', ''));
    }
    if (typeof value === 'number') {
      return value;
    }
    // Default fallback
    return dimension === 'width' ? Math.round(window.innerWidth * 0.3) : Math.round(window.innerHeight * 0.4);
  };

  const finalWidth = convertPercentageToPixels(width, 'width');
  const finalHeight = convertPercentageToPixels(height, 'height');

  return { finalWidth, finalHeight };
}

export { getTokensFromUrl, buildWebToolUrl, runWebTool, runWebToolFromToolConfig, getRequiredSelectionType, openInIframe, setIframeTarget };
